/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useState, useEffect, useContext } from "react";
import produce from "immer";
import { useParams } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import { LocationsContext } from ".";
import fetchHelper from "../../services/fetchHelper";

const CustomEsignStatement = ({ statement, onDelete, onUpdate }) => {
  const [editMode, setEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [statementText, setStatementText] = useState(statement.conclusionStatement);

  const onChangeStatement = (e) => {
    const { value } = e.target;
    setStatementText(value);
  };

  return (
    <div className="row rowStretch">
      <div className="col col-s-100 col50">
        <label className="inCal">
          {editMode ? (
            <>
              <span>Edit {statement.conclusionStatement}</span>
              <input type="text" value={statementText} onChange={onChangeStatement} />
            </>
          ) : (
            <>
              <span>{statementText}</span>
              <select>
                <option value="">Select</option>
              </select>
            </>
          )}
        </label>
      </div>
      <div className="col col-s-100 col50 custom-esign-statements-btn">
        <ul style={{ display: "flex", listStyle: "none", padding: "4px 0px" }}>
          {editMode ? (
            <>
              <li>
                <button
                  className="btn btnSuccess marginTopFix"
                  onClick={async () => {
                    setIsLoading(true);
                    await onUpdate(statement.id, statementText);
                    setEditMode(false);
                    setIsLoading(false);
                  }}
                >
                  {isLoading ? (
                    <div
                      id="results-loading-icon"
                      className="saving-white-ico icon-64"
                      style={{ width: 15, height: 15, marginRight: 3 }}
                    />
                  ) : null}
                  Save
                </button>
              </li>
              <li>
                <button
                  className="btn btnNoTxt marginTopFix"
                  onClick={(e) => {
                    setStatementText(statement.conclusionStatement);
                    setEditMode(false);
                  }}
                >
                  <i className="close-flat-ico icon-64" style={{ width: 20, height: 20 }}></i>
                </button>
              </li>
              <li>
                <button className="btn btnUrgent marginTopFix" onClick={() => onDelete(statement.id)}>
                  Delete
                </button>
              </li>
            </>
          ) : (
            <li>
              <button
                className="btn btn-teal marginTopFix"
                onClick={(e) => {
                  setEditMode(true);
                }}
              >
                Edit
              </button>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

const FacilityEsignReport = () => {
  const { i } = useParams();
  const { addToast } = useToasts();
  const { location } = useContext(LocationsContext);
  const [customEsignStatements, setCustomEsignStatements] = useState([]);
  const [customEsignStatementGroups, setCustomEsignStatementGroups] = useState([]);

  useEffect(() => {
    fetchCustomEsignStatements();
    fetchCustomEsignStatementGroups();
  }, []);

  const fetchCustomEsignStatements = async () => {
    try {
      const response = await fetchHelper.get({
        url: `/patientmanagement/rest/esignstatement?where={"location.id": {"$eq":${i}}, "iseditable": {"$eq": true}}&sort=id,esignStatementGroup.id`,
      });
      setCustomEsignStatements(response);
    } catch ({ errors }) {
      errors &&
        errors.forEach((err) => {
          addToast(err.msg, {
            appearance: "error",
            autoDismiss: false,
          });
        });
    }
  };

  const fetchCustomEsignStatementGroups = async () => {
    try {
      const response = await fetchHelper.get({
        url: `/patientmanagement/rest/esignStatementGroup?where={"location.id": {"$eq":${i}}}&sort=id`,
      });
      setCustomEsignStatementGroups(response);
    } catch ({ errors }) {
      errors &&
        errors.forEach((err) => {
          addToast(err.msg, {
            appearance: "error",
            autoDismiss: false,
          });
        });
    }
  };

  const onDeleteCustomEsignStatement = async (statementId) => {
    try {
      await fetchHelper.delete("/patientmanagement/rest/esignstatement/" + statementId);
      setCustomEsignStatements((prev) =>
        produce(prev, (draft) => {
          const findIndex = draft.findIndex((i) => i.id === statementId);
          if (findIndex !== -1) draft.splice(findIndex, 1);
        })
      );
    } catch ({ errors }) {
      errors &&
        errors.forEach((err) => {
          addToast(err.msg, {
            appearance: "error",
            autoDismiss: false,
          });
        });
    }
  };

  const onUpdateCustomEsignStatement = async (statementId, conclusionStatement) => {
    try {
      await fetchHelper.put("/patientmanagement/rest/esignstatement/" + statementId, {
        id: statementId,
        conclusionStatement,
      });
      setCustomEsignStatements((prev) =>
        produce(prev, (draft) => {
          const findIndex = draft.findIndex((i) => i.id === statementId);
          if (findIndex !== -1) draft[findIndex].conclusionStatement = conclusionStatement;
        })
      );
    } catch ({ errors }) {
      errors.forEach((err) => {
        addToast(err.msg, {
          appearance: "error",
          autoDismiss: false,
        });
      });
    }
  };

  return (
    <div className="tabContent ptInformation">
      <div className="content">
        <div className="row rowStretch">
          <div className="col col100">
            <div className="grpHdr">
              <h3>Default E-SIGN Statements</h3>
            </div>

            {location?.defaultEsignStatements?.length > 0 ? (
              location?.defaultEsignStatements?.map((statement) => (
                <label className="chk-option" style={{ display: "inline-flex", alignItems: "center" }}>
                  <input type="checkbox" className="chk" />
                  <span className="chkmrk" style={{ width: "initial" }}></span>
                  <span className="lblTxt">{statement.conclusionStatement}</span>
                </label>
              ))
            ) : (
              <div style={{ marginBottom: 20 }}>No Default E-Sign Statement</div>
            )}

            <div className="grpHdr">
              <h3>E-SIGN Statement Groups</h3>
            </div>

            {customEsignStatementGroups.length > 0 ? (
              customEsignStatementGroups.map((statement) => (
                <label className="chk-option" style={{ display: "inline-flex", alignItems: "center" }}>
                  <input type="checkbox" className="chk" />
                  <span className="chkmrk" style={{ width: "initial" }}></span>
                  <span className="lblTxt">{statement.conclusionStatement}</span>
                </label>
              ))
            ) : (
              <div style={{ marginBottom: 20 }}>Loading Groups...</div>
            )}

            <div className="grpHdr custom-esign-statements-hdr">
              <h3>Custom E-SIGN Statements</h3>
            </div>

            <div>
              <div className="row rowStretch custom-esign-statements">
                <div className="col col100">
                  {customEsignStatements.length > 0 ? (
                    customEsignStatements.map((statement) => (
                      <CustomEsignStatement
                        key={statement.id}
                        statement={statement}
                        onDelete={onDeleteCustomEsignStatement}
                        onUpdate={onUpdateCustomEsignStatement}
                      />
                    ))
                  ) : (
                    <span>Loading Custom E-Sign Statement...</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* {!information.openForm ? (
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: "rgba(255,255,255,0.25)",
          }}
        />
      ) : null} */}
    </div>
  );
};

export default FacilityEsignReport;
